import { useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { Box } from "ui/box";
import { IconButton } from "ui/icon-button";
import { Checkbox } from "ui/checkbox";
import { Select, SelectOptionContent, type OptionType } from "ui/select";
import { Switch } from "ui/switch";
import { TextButton } from "ui/text-button";

import { DEFAULT_PROGRAM_TYPE } from "hooks/api/dictionary";
import { useCampaignTemplates } from "hooks/api/use-campaign-templates";

import { Button } from "ui/button";
import { DialogActions } from "ui/dialog";
import { useCampaignWithWriterContext } from "components/modals/create-campaign-with-writer-modal/context/campaign-with-writer-context";
import {
  CAMPAIGN_TEMPLATE_HELPER_TEXT,
  PROGRAM_STRATEGY_OPTIONS,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.constants";
import {
  getTemplateSummary,
  getNextStep,
  shouldSkipCreativesStep,
} from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.helper";
import type { CreateCampaignWithWriterFormData } from "components/modals/create-campaign-with-writer-modal/create-campaign-with-writer-modal.types";
import { CampaignTemplateSummaryModal } from "containers/admin/clients/client/campaign-templates-page/campaign-template-summary-modal";

import { getClientPaths } from "utilities/app-routes";

import Icons from "assets";

type CampaignSetupStepProps = {
  hasCreatives: boolean;
};

export const CampaignSetupStep = ({ hasCreatives }: CampaignSetupStepProps) => {
  const { control, watch, setValue } = useFormContext<CreateCampaignWithWriterFormData>();
  const [summaryTemplateId, setSummaryTemplateId] = useState<string | null>(null);

  const selectedProgramStrategy = watch("programStrategy");

  const isTransferCampaignDisabled = selectedProgramStrategy !== DEFAULT_PROGRAM_TYPE;

  const isCampaignTemplateEnabled = Boolean(selectedProgramStrategy);

  const campaignTemplateQueryParams = useMemo(
    () => ({
      filters: {
        programType: selectedProgramStrategy ? [selectedProgramStrategy] : undefined,
      },
    }),
    [selectedProgramStrategy],
  );

  const { data: campaignTemplates = [], isLoading: isLoadingCampaignTemplates } =
    useCampaignTemplates(campaignTemplateQueryParams, {
      enabled: isCampaignTemplateEnabled,
    });

  const { clientCampaignTemplatesPath } = getClientPaths();

  const resetDeliverables = () => {
    setValue("deliverables", []);
  };

  const resetCampaignTemplate = () => {
    setValue("campaignTemplateId", "");
    setValue("includeCreatives", false);
    resetDeliverables();
  };

  const handleProgramStrategyChange = (onChange: (value: string | undefined) => void) => {
    return (selectedOption: OptionType | null) => {
      onChange(selectedOption?.value as string | undefined);
      setValue("transferStudentStatusFlag", false);
      resetCampaignTemplate();
    };
  };

  const campaignTemplateOptions = campaignTemplates
    .filter((template) => {
      if (!template.id || !template.name) {
        return false;
      }

      const summary = getTemplateSummary(template.data);

      return summary.audienceSegments.length > 0 && Boolean(summary.campaignObjective);
    })
    .map((template) => ({
      label: template.name as string,
      value: template.id as string,
    }));

  const renderGenerateSummaryButton = (option: { label: string; value: string }) => (
    <IconButton
      color="inherit"
      variant="outlined"
      size="small"
      aria-label={`Generate summary for ${option.label}`}
      onClick={(event) => {
        event.stopPropagation();
        setSummaryTemplateId(option.value);
      }}>
      <Icons.Summary />
    </IconButton>
  );

  return (
    <>
      <div className="flex flex-col gap-4">
        <p className="text-gray-600">Please choose campaign brief from existing briefs.</p>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <Controller
              name="programStrategy"
              control={control}
              render={({ field: { onChange, value, onBlur, name, ref } }) => (
                <Select
                  label={<span className="font-bold">Program strategy</span>}
                  selectName="programStrategy"
                  required
                  options={[...PROGRAM_STRATEGY_OPTIONS]}
                  ariaLabel="Select program strategy"
                  name={name}
                  ref={ref}
                  onBlur={onBlur}
                  value={PROGRAM_STRATEGY_OPTIONS.find((option) => option.value === value) || null}
                  onChange={handleProgramStrategyChange(onChange)}
                  startIcon={<Icons.Search className="text-aqua-600" />}
                />
              )}
            />

            <Controller
              name="transferStudentStatusFlag"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Switch
                  toggleName="Transfer campaign"
                  checked={Boolean(value)}
                  onChange={onChange}
                  disabled={isTransferCampaignDisabled}
                />
              )}
            />
          </div>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Controller
              name="campaignTemplateId"
              control={control}
              render={({ field: { onChange, value, onBlur, name, ref } }) => (
                <Select
                  label={<span className="font-bold">Campaign brief</span>}
                  selectName="campaignTemplateId"
                  placeholder={isLoadingCampaignTemplates ? "Loading templates..." : ""}
                  helperText={CAMPAIGN_TEMPLATE_HELPER_TEXT}
                  required
                  disabled={!isCampaignTemplateEnabled || isLoadingCampaignTemplates}
                  options={campaignTemplateOptions}
                  ariaLabel="Select campaign brief"
                  name={name}
                  ref={ref}
                  onBlur={onBlur}
                  value={campaignTemplateOptions.find((option) => option.value === value) || null}
                  onChange={(selectedOption) => {
                    onChange(selectedOption?.value ?? "");
                    setValue("includeCreatives", false);
                  }}
                  startIcon={<Icons.Search className="text-aqua-600" />}
                  formatOptionLabel={(option, { context }) =>
                    context === "menu" ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                          gap: 2,
                        }}>
                        <SelectOptionContent option={option} />
                        {renderGenerateSummaryButton(option)}
                      </Box>
                    ) : (
                      option.label
                    )
                  }
                  renderSelectedValueAction={renderGenerateSummaryButton}
                />
              )}
            />

            {hasCreatives && (
              <Controller
                name="includeCreatives"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Checkbox
                    checked={Boolean(value)}
                    onChange={onChange}
                    label="Include creatives from campaign brief"
                    size="sm"
                  />
                )}
              />
            )}

            <TextButton
              href={clientCampaignTemplatesPath}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              endIcon={<Icons.Forward />}>
              View campaign briefs
            </TextButton>
          </Box>
        </div>
      </div>
      {summaryTemplateId && (
        <CampaignTemplateSummaryModal
          source={{ type: "template", templateId: summaryTemplateId }}
          onClose={() => setSummaryTemplateId(null)}
        />
      )}
    </>
  );
};

type CampaignSetupStepActionsProps = {
  hasCreatives: boolean;
};

export const CampaignSetupStepActions = ({ hasCreatives }: CampaignSetupStepActionsProps) => {
  const { watch } = useFormContext<CreateCampaignWithWriterFormData>();
  const includeCreatives = watch("includeCreatives");
  const skipCreativesStep = shouldSkipCreativesStep(hasCreatives, includeCreatives);
  const { step, goTo, setIsOpen } = useCampaignWithWriterContext();
  const handleNext = () => goTo(getNextStep(step, skipCreativesStep));
  const handleClose = () => setIsOpen(false);

  const selectedProgramStrategy = watch("programStrategy");
  const selectedCampaignTemplateId = watch("campaignTemplateId");

  const isContinueDisabled = !(selectedProgramStrategy && selectedCampaignTemplateId);

  return (
    <DialogActions
      type="steps"
      secondary={
        <Button size="large" variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
      }
      primary={
        <Button size="large" disabled={isContinueDisabled} onClick={handleNext}>
          Continue with details
        </Button>
      }
    />
  );
};
